import React, { useState, useEffect } from 'react';
import {
    Box,
    Grid,
    Card,
    CardContent,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Alert,
    Paper,
    Tabs,
    Tab,
    Chip,
    Button,
    ButtonGroup,
    IconButton,
    Tooltip
} from '@mui/material';
import {
    Refresh,
    Download,
    FilterList
} from '@mui/icons-material';
import { Chart } from 'react-google-charts';
import { useUser } from '../../contexts/UserContext';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import {
    getAnalyticsDashboard,
    processAnalyticsForCharts,
    formatAnalyticsForExport
} from '../../utils/api/analyticsApi';
import {
    ScoreDistributionChart,
    TimeSeriesChart,
    ConsultantComparisonChart,
    EvaluationVolumeChart,
    CompetencyProgressionChart,
    OverviewMetrics
} from './AnalyticsCharts';
import AnalyticsFilters, { AnalyticsFiltersState } from './AnalyticsFilters';

const Analytics: React.FC = () => {
    const { user, isGlobalAdmin, isTenantAdmin } = useUser();
    useDocumentTitle('EvalPro Analytics');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [dateRange, setDateRange] = useState(30);
    const [tabValue, setTabValue] = useState(0);
    const [timeSeriesGranularity, setTimeSeriesGranularity] = useState<'daily' | 'weekly' | 'monthly'>('daily');
    const [volumeType, setVolumeType] = useState<'monthly' | 'quarterly' | 'company'>('monthly');
    const [refreshing, setRefreshing] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [activeFilters, setActiveFilters] = useState<AnalyticsFiltersState>({
        consultants: [],
        categories: [],
        statuses: [],
        companies: [],
        scoreRange: { min: null, max: null },
        customDateRange: { start: null, end: null },
        companySearch: ''
    });

    // Raw analytics data from backend
    const [rawAnalyticsData, setRawAnalyticsData] = useState<any>(null);
    // Processed data for charts
    const [chartData, setChartData] = useState<any>({
        overview: null,
        scoreDistribution: null,
        timeSeries: null,
        consultantComparison: null,
        evaluationVolume: null,
        competencyProgression: null
    });

    useEffect(() => {
        if (user) {
            loadAnalytics();
        }
    }, [user, dateRange, activeFilters]);

    useEffect(() => {
        // Process data when raw data changes
        if (rawAnalyticsData) {
            const processed = processAnalyticsForCharts(rawAnalyticsData);
            setChartData(processed);
        }
    }, [rawAnalyticsData]);

    const loadAnalytics = async (forceRefresh = false) => {
        try {
            if (forceRefresh) {
                setRefreshing(true);
            } else {
                setLoading(true);
            }
            setError('');

            console.log('Loading analytics data with date range:', dateRange);

            const result = await getAnalyticsDashboard({
                dateRange: dateRange,
                filters: activeFilters as any // Type assertion for now until api types are aligned
            });

            if (result.success) {
                setRawAnalyticsData(result.data);
                console.log('Analytics data loaded successfully:', result.data);
            } else {
                setError(result.error || 'Failed to load analytics data');
                console.error('Failed to load analytics:', result.error);
            }

        } catch (error: any) {
            console.error('Error loading analytics:', error);
            setError(`Failed to load analytics data: ${error.message}`);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    const handleRefresh = () => {
        loadAnalytics(true);
    };

    const handleExport = (format = 'csv') => {
        if (!rawAnalyticsData) {
            alert('No data available for export');
            return;
        }

        const exportData = formatAnalyticsForExport(rawAnalyticsData, format);
        const blob = new Blob([exportData], {
            type: format === 'csv' ? 'text/csv' : 'application/json'
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `analytics_${new Date().toISOString().split('T')[0]}.${format}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const handleFiltersChange = (newFilters: AnalyticsFiltersState) => {
        setActiveFilters(newFilters);
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ flexGrow: 1 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
                <Box>
                    <Typography variant="h4" gutterBottom>
                        EvalPro Analytics
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        {isGlobalAdmin() ? 'Global' : 'Tenant'} presentation evaluation insights and performance metrics
                    </Typography>
                    {rawAnalyticsData && (
                        <Box sx={{ mt: 1 }}>
                            <Chip
                                label={`Generated: ${new Date(rawAnalyticsData.generatedAt || new Date()).toLocaleString()}`}
                                size="small"
                                variant="outlined"
                            />
                        </Box>
                    )}
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <FormControl sx={{ minWidth: 150 }}>
                        <InputLabel>Date Range</InputLabel>
                        <Select
                            value={dateRange}
                            onChange={(e) => setDateRange(Number(e.target.value))}
                            label="Date Range"
                        >
                            <MenuItem value={7}>Last 7 days</MenuItem>
                            <MenuItem value={30}>Last 30 days</MenuItem>
                            <MenuItem value={90}>Last 90 days</MenuItem>
                            <MenuItem value={180}>Last 6 months</MenuItem>
                            <MenuItem value={365}>Last year</MenuItem>
                        </Select>
                    </FormControl>

                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Refresh Data">
                            <IconButton
                                onClick={handleRefresh}
                                disabled={refreshing}
                                color="primary"
                            >
                                <Refresh sx={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
                            </IconButton>
                        </Tooltip>

                        <Tooltip title="Export Data">
                            <IconButton
                                onClick={() => handleExport('csv')}
                                disabled={!rawAnalyticsData}
                                color="primary"
                            >
                                <Download />
                            </IconButton>
                        </Tooltip>

                        <Tooltip title="Toggle Filters">
                            <IconButton
                                onClick={() => setShowFilters(!showFilters)}
                                color={showFilters ? "primary" : "default"}
                            >
                                <FilterList />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {/* Advanced Filters */}
            {showFilters && (
                <AnalyticsFilters
                    filters={activeFilters}
                    onFiltersChange={handleFiltersChange}
                    loading={loading}
                />
            )}

            {/* Tabs */}
            <Paper sx={{ mb: 3 }}>
                <Tabs value={tabValue} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
                    <Tab label="Overview" />
                    <Tab label="Performance Trends" />
                    <Tab label="Score Distribution" />
                    <Tab label="Consultants" />
                    <Tab label="Evaluation Volume" />
                    <Tab label="Competency Growth" />
                </Tabs>
            </Paper>

            {/* Tab Content */}
            {tabValue === 0 && (
                <Box>
                    <OverviewMetrics
                        overview={chartData.overview}
                        loading={loading}
                        error={error}
                    />

                    <Grid container spacing={3} sx={{ mt: 2 }}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <ScoreDistributionChart
                                data={chartData.scoreDistribution?.overall}
                                loading={loading}
                                error={error}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                            <EvaluationVolumeChart
                                data={chartData.evaluationVolume?.byMonth}
                                loading={loading}
                                error={error}
                                volumeType="monthly"
                            />
                        </Grid>
                    </Grid>
                </Box>
            )}

            {tabValue === 1 && (
                <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                        <ButtonGroup size="small">
                            <Button
                                variant={timeSeriesGranularity === 'daily' ? 'contained' : 'outlined'}
                                onClick={() => setTimeSeriesGranularity('daily')}
                            >
                                Daily
                            </Button>
                            <Button
                                variant={timeSeriesGranularity === 'weekly' ? 'contained' : 'outlined'}
                                onClick={() => setTimeSeriesGranularity('weekly')}
                            >
                                Weekly
                            </Button>
                            <Button
                                variant={timeSeriesGranularity === 'monthly' ? 'contained' : 'outlined'}
                                onClick={() => setTimeSeriesGranularity('monthly')}
                            >
                                Monthly
                            </Button>
                        </ButtonGroup>
                    </Box>

                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12 }}>
                            <TimeSeriesChart
                                data={chartData.timeSeries?.[timeSeriesGranularity]}
                                loading={loading}
                                error={error}
                                granularity={timeSeriesGranularity}
                            />
                        </Grid>
                    </Grid>
                </Box>
            )}

            {tabValue === 2 && (
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12 }}>
                        <ScoreDistributionChart
                            data={chartData.scoreDistribution?.overall}
                            loading={loading}
                            error={error}
                        />
                    </Grid>

                    {chartData.scoreDistribution?.byCategory && Object.keys(chartData.scoreDistribution.byCategory).map((category: string) => (
                        <Grid size={{ xs: 12, md: 6 }} key={category}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        {category} Score Distribution
                                    </Typography>
                                    <Chart
                                        chartType="ColumnChart"
                                        data={chartData.scoreDistribution.byCategory[category]}
                                        options={{
                                            title: `${category} Scores`,
                                            backgroundColor: 'transparent',
                                            colors: ['#1976d2'],
                                            chartArea: { left: 60, top: 40, width: '80%', height: '75%' },
                                            hAxis: { title: 'Score Range' },
                                            vAxis: { title: 'Count' },
                                            legend: { position: 'none' },
                                            height: 250
                                        }}
                                        width="100%"
                                        height="250px"
                                    />
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}

            {tabValue === 3 && (
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12 }}>
                        <ConsultantComparisonChart
                            data={chartData.consultantComparison}
                            loading={loading}
                            error={error}
                        />
                    </Grid>
                </Grid>
            )}

            {tabValue === 4 && (
                <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                        <ButtonGroup size="small">
                            <Button
                                variant={volumeType === 'monthly' ? 'contained' : 'outlined'}
                                onClick={() => setVolumeType('monthly')}
                            >
                                Monthly
                            </Button>
                            <Button
                                variant={volumeType === 'quarterly' ? 'contained' : 'outlined'}
                                onClick={() => setVolumeType('quarterly')}
                            >
                                Quarterly
                            </Button>
                            <Button
                                variant={volumeType === 'company' ? 'contained' : 'outlined'}
                                onClick={() => setVolumeType('company')}
                            >
                                By Company
                            </Button>
                        </ButtonGroup>
                    </Box>

                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12 }}>
                            <EvaluationVolumeChart
                                data={chartData.evaluationVolume?.[`by${volumeType.charAt(0).toUpperCase() + volumeType.slice(1)}`]}
                                loading={loading}
                                error={error}
                                volumeType={volumeType}
                            />
                        </Grid>
                    </Grid>
                </Box>
            )}

            {tabValue === 5 && (
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12 }}>
                        <CompetencyProgressionChart
                            data={chartData.competencyProgression}
                            loading={loading}
                            error={error}
                        />
                    </Grid>
                </Grid>
            )}

            <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
        </Box>
    );
};

export default Analytics;
